/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router';
import isEmpty from 'lodash/isEmpty';

import {
    ContentBlock,
    Stack,
    ReadMoreBlock,
    Tabs,
    TabPanel,
    TextBlock,
    Icon,
    DisplayHtmlContent,
    useFlags,
    SecurityHelper,
} from 'sarsaparilla';
import { HeroImageGallery } from 'shared-ui/hero-image-gallery';
import { FacilityPageHeader } from 'shared-ui/components/FacilityPageHeader/FacilityPageHeader';
import { BackToLink } from 'shared-ui/components/BackToLink/BackToLink';
import { FacilityAlertBanner } from 'shared-ui/components/FacilityAlertBanner/FacilityAlertBanner';

import { InventoryReviewsSection } from 'ui-rating-review';

import { ToursTabContainer } from '../../containers/FacilityPage/ToursTabContainer';
import { FacilityInfoTabContainer } from '../../containers/FacilityPage/FacilityInfoTabContainer';
import { FeesTabContainer } from '../../containers/FacilityPage/FeesTabContainer.jsx';
import { BookingTabContainer } from '../../containers/FacilityPage/BookingTabContainer.jsx';

import { Infographic } from './Infographic';

import { tabNameToIndex, tabIndexToName } from '../../utils';
import { facilityAlertsType } from '../../types';
import { INFOGRAPHIC } from '../../constants';

const facilityInfoTabWithIcon = (
    <div>
        <Icon iconName="warning" />
        <span>Need to Know</span>
    </div>
);

export function FacilityPage({
    isLoading,
    tabFromQuery,
    showBackToSearch = false,
    onBackToSearchClick,
    alerts,
    facilityId,
    facilityName,
    facilityDescription,
    facilityNearLocation,
    parentName,
    parentUrl,
    rating,
    reviewCount,
    mobileCoverageRating,
    galleryImages,
    heroImage,
    facilityImageLoadingStatus,
}) {
    const [, setSearchParams] = useSearchParams();
    const { tePassesBanner = {} } = useFlags();
    const [activeTab, setActiveTab] = useState(0);
    const infographicInfo = facilityId ? INFOGRAPHIC[facilityId] : null;
    const showInfographic = !isLoading && !!infographicInfo && tePassesBanner[facilityId];
    const isWidget = SecurityHelper.isWidgetSession();

    useEffect(() => {
        if (tabFromQuery) {
            const tabIndex = tabNameToIndex(tabFromQuery);
            setActiveTab(tabIndex);
        }
    }, [tabFromQuery]);

    function handleTabChange(index) {
        setActiveTab(index);
        const tabName = tabIndexToName(index);
        setSearchParams({ tab: tabName });
    }

    const facilityInfoTab = isEmpty(alerts) ? 'Need to Know' : facilityInfoTabWithIcon;

    return (
        <main className="te-facility-page mb-6">
            {!isWidget && (
                <HeroImageGallery
                    heroImage={heroImage}
                    galleryImages={galleryImages}
                    status={facilityImageLoadingStatus}
                />
            )}

            <FacilityAlertBanner alerts={alerts} />

            <ContentBlock>
                {showBackToSearch && (
                    <div className="pt-one-and-half">
                        <BackToLink
                            backTo="Search Results"
                            onClick={onBackToSearchClick}
                        />
                    </div>
                )}
                <Stack space="lg" className={!isWidget ? 'mt-5' : ''}>
                    {!isWidget && (
                        <>
                            <FacilityPageHeader
                                isLoading={isLoading}
                                heading={facilityName}
                                parentLinkText={parentName}
                                parentLinkUrl={parentUrl}
                                nearText={`Near ${facilityNearLocation}`}
                                rating={rating}
                                reviewCount={reviewCount}
                                mobileCoverageRating={mobileCoverageRating}
                                favoriteFacilityId={facilityId}
                                favoriteEntityId={facilityId}
                                favoriteEntityType={'timedentry'}
                                onClickRatings={() => handleTabChange(4)}
                            />

                            <TextBlock>
                                <ReadMoreBlock
                                    isLoading={isLoading}
                                    buttonAriaLabel="Read more: Overview"
                                    id="about-the-facility"
                                >
                                    <DisplayHtmlContent html={facilityDescription} />
                                </ReadMoreBlock>
                            </TextBlock>

                            {showInfographic && (
                                <Infographic
                                    facilityId={facilityId}
                                    showInfographic={showInfographic}
                                    data={infographicInfo?.data}
                                />
                            )}
                        </>
                    )}
                    <Tabs
                        activeIndex={activeTab}
                        onChange={handleTabChange}
                        stretchTabsToFit
                        appearance="underline"
                        className="te-tabs"
                    >
                        <TabPanel label="Tickets">
                            <ToursTabContainer handleTabChange={handleTabChange} />
                        </TabPanel>

                        <TabPanel label="Seasons & Booking">
                            <BookingTabContainer />
                        </TabPanel>

                        <TabPanel label="Fees & Cancellations">
                            <FeesTabContainer />
                        </TabPanel>
                        <TabPanel label={facilityInfoTab}>
                            <FacilityInfoTabContainer />
                        </TabPanel>
                        <TabPanel label="Ratings & Reviews" isHidden={!reviewCount}>
                            <InventoryReviewsSection
                                locationId={facilityId}
                                locationType="Timed Entry"
                            />
                        </TabPanel>
                    </Tabs>
                </Stack>
            </ContentBlock>
        </main>
    );
}

FacilityPage.propTypes = {
    tabFromQuery: PropTypes.string,
    showBackToSearch: PropTypes.bool,
    onBackToSearchClick: PropTypes.func,
    alerts: facilityAlertsType,
    facilityId: PropTypes.string,
    facilityName: PropTypes.string,
    facilityDescription: PropTypes.string,
    facilityNearLocation: PropTypes.string,
    parentName: PropTypes.string,
    parentUrl: PropTypes.string,
    rating: PropTypes.number,
    reviewCount: PropTypes.number,
    mobileCoverageRating: PropTypes.number,
    galleryImages: PropTypes.arrayOf(PropTypes.object),
    heroImage: PropTypes.objectOf(PropTypes.string),
    facilityImageLoadingStatus: PropTypes.string,
    isLoading: PropTypes.bool,
};

// cSpell:ignore timedentry
